import React, { useState, useEffect } from 'react';
import { ArrowLeft, Star, Heart, Users, Check, MessageCircle, Send, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CandidateWithSlides, Evaluator, Evaluation, EvaluationComment } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

interface EvaluationDetailProps {
  candidate: CandidateWithSlides;
  evaluator: Evaluator;
  onBack: () => void;
  onEvaluationSubmitted: () => void;
  isOwn?: boolean; // 自分の研究かどうか
}

export default function EvaluationDetail({
  candidate,
  evaluator,
  onBack,
  onEvaluationSubmitted,
  isOwn = false,
}: EvaluationDetailProps) {
  const [liked, setLiked] = useState(false);
  const [wantToParticipate, setWantToParticipate] = useState(false);
  const [savingLike, setSavingLike] = useState(false);
  const [savingParticipate, setSavingParticipate] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState<Evaluation | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [allEvaluations, setAllEvaluations] = useState<Evaluation[]>([]);
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSecretComment, setIsSecretComment] = useState(false);
  const [isSecretReply, setIsSecretReply] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [commentAuthors, setCommentAuthors] = useState<{[key: string]: {name: string, username?: string}}>({});
  const [evaluatorById, setEvaluatorById] = useState<Record<string, {name: string; username?: string}>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [collapsedTopics, setCollapsedTopics] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadExistingEvaluation();
    loadAllEvaluations();
  }, []);

  useEffect(() => {
    // Load comments when we have any evaluations, not just the current user's
    if (allEvaluations.length > 0) {
      loadComments();
    }
  }, [allEvaluations]);

  const loadExistingEvaluation = async () => {
    try {
      const { data } = await supabase
        .from('evaluations')
        .select('*')
        .eq('candidate_id', candidate.id)
        .eq('evaluator_id', evaluator.id)
        .maybeSingle();

      if (data) {
        setExistingEvaluation(data);
        setLiked(data.liked);
        setWantToParticipate(data.want_to_participate);
        setRating(Math.max(0, Math.min(5, data.rating || 0)));
      }
    } catch (err) {
      // No existing evaluation is fine
    }
  };

  // Ensure an evaluation row exists; if not, create with minimal defaults
  const ensureEvaluation = async (): Promise<Evaluation> => {
    if (existingEvaluation) return existingEvaluation;
    const { data } = await supabase
      .from('evaluations')
      .select('*')
      .eq('candidate_id', candidate.id)
      .eq('evaluator_id', evaluator.id)
      .maybeSingle();
    if (data) {
      setExistingEvaluation(data as Evaluation);
      setRating(Math.max(0, Math.min(5, (data as any).rating || 0)));
      return data as Evaluation;
    }
    const { data: created, error } = await supabase
      .from('evaluations')
      .insert({
        candidate_id: candidate.id,
        evaluator_id: evaluator.id,
        rating: 0, // 0 = 評価なし
        liked: false,
        want_to_participate: false,
      })
      .select()
      .single();
    if (error) throw error;
    setExistingEvaluation(created as Evaluation);
    setRating(0);
    // ensure comments area becomes available
    setAllEvaluations(prev => prev.length ? prev : [created as Evaluation]);
    return created as Evaluation;
  };

  const savePartial = async (patch: Partial<Evaluation>) => {
    const evalRow = await ensureEvaluation();
    const { error } = await supabase
      .from('evaluations')
      .update({ ...patch })
      .eq('id', evalRow.id);
    if (error) throw error;
    setExistingEvaluation({ ...evalRow, ...patch } as Evaluation);
    if (typeof patch.rating === 'number') {
      setRating(Math.max(0, Math.min(5, patch.rating)));
    }
  };

  const loadAllEvaluations = async () => {
    try {
      // Load all evaluations for this candidate to get comment access
      const { data } = await supabase
        .from('evaluations')
        .select('*')
        .eq('candidate_id', candidate.id);

      if (data && data.length > 0) {
        setAllEvaluations(data);
        const evaluatorIds = Array.from(new Set((data as any[]).map(e => e.evaluator_id).filter(Boolean)));
        if (evaluatorIds.length) {
          const { data: infos } = await supabase.from('evaluators').select('id, name, username').in('id', evaluatorIds);
          const map: Record<string, {name: string; username?: string}> = {};
          (infos || []).forEach((ev: any) => { map[ev.id] = { name: ev.name, username: ev.username || undefined }; });
          setEvaluatorById(map);
        }
      }
    } catch (err) {
      console.error('Error loading evaluations:', err);
    }
  };

  const loadComments = async () => {
    // Load comments if there are any evaluations; otherwise, keep empty list
    if (allEvaluations.length === 0) {
      setComments([]);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const uid = user?.id || null;
      const uemail = user?.email || null;
      setCurrentUserId(uid);
      setCurrentUserEmail(uemail);

      // Load comments from all evaluations for this candidate with server-side filtering where possible
      const evaluationIds = allEvaluations.map(e => e.id);
      let combined: EvaluationComment[] = [];

      if (uemail && uemail === candidate.email) {
        const { data, error } = await supabase
          .from('evaluation_comments')
          .select('*')
          .in('evaluation_id', evaluationIds)
          .order('created_at', { ascending: true });
        if (error) throw error;
        combined = (data || []) as any;
      } else {
        const { data: pub, error: e1 } = await supabase
          .from('evaluation_comments')
          .select('*')
          .in('evaluation_id', evaluationIds)
          .eq('is_secret', false)
          .order('created_at', { ascending: true });
        if (e1) throw e1;
        combined = (pub || []) as any;

        if (uid) {
          const { data: own, error: e2 } = await supabase
            .from('evaluation_comments')
            .select('*')
            .in('evaluation_id', evaluationIds)
            .eq('is_secret', true)
            .eq('user_id', uid)
            .order('created_at', { ascending: true });
          if (e2) throw e2;
          combined = combined.concat((own || []) as any);
        }
      }

      const visible = combined.filter((c) => canViewComment(c, uid, uemail));
      setComments(visible);
      
      // Set author information for current user's comments only
      const authorMap: {[key: string]: {name: string, username?: string}} = {};
      for (const comment of visible) {
        if (uid && comment.user_id === uid && !authorMap[comment.user_id]) {
          authorMap[comment.user_id] = {
            name: evaluator.name,
            username: evaluator.username
          };
        }
      }
      
      setCommentAuthors(authorMap);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSubmitEvaluation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const evaluationData = {
        candidate_id: candidate.id,
        evaluator_id: evaluator.id,
        // rating: 0-5（0は「評価なし」）
        rating: Math.max(0, Math.min(5, rating)),
        liked,
        want_to_participate: wantToParticipate
      };

      if (existingEvaluation) {
        // Update existing evaluation
        const { data: updated, error } = await supabase
          .from('evaluations')
          .update(evaluationData)
          .eq('id', existingEvaluation.id)
          .select()
          .single();

        if (error) throw error;
        if (updated) {
          setExistingEvaluation(updated as Evaluation);
          setRating(Math.max(0, Math.min(5, (updated as any).rating || 0)));
        }
      } else {
        // Create new evaluation
        const { data: created, error } = await supabase
          .from('evaluations')
          .insert(evaluationData)
          .select()
          .single();

        if (error) throw error;
        if (created) {
          setExistingEvaluation(created as Evaluation);
          setRating(Math.max(0, Math.min(5, (created as any).rating || 0)));
        }
      }

      setSuccess(true);
      // keep aggregates fresh
      await loadAllEvaluations();
      setTimeout(() => {
        onEvaluationSubmitted();
        onBack();
      }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() && !newTopic.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Ensure an evaluation exists for this evaluator-candidate pair
      let evaluationId = existingEvaluation?.id;
      if (!evaluationId) {
        // Try to find existing evaluation by this evaluator
        const { data: existing } = await supabase
          .from('evaluations')
          .select('id')
          .eq('candidate_id', candidate.id)
          .eq('evaluator_id', evaluator.id)
          .maybeSingle();
        if (existing) {
          evaluationId = existing.id as string;
        } else {
          // Create minimal evaluation record to attach comments
          const { data: created, error: evalError } = await supabase
            .from('evaluations')
            .insert({
              candidate_id: candidate.id,
              evaluator_id: evaluator.id,
              rating: 0,
              liked: false,
              want_to_participate: false
            })
            .select()
            .single();
          if (evalError) throw evalError;
          evaluationId = created.id as string;
          setExistingEvaluation(created as Evaluation);
          setAllEvaluations(prev => [...prev, created as Evaluation]);
        }
      }

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId!,
          user_id: user.id,
          user_type: 'evaluator',
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          is_secret: isSecretComment,
          parent_comment_id: null,
          topic: newTopic.trim() || 'general'
        });

      if (error) throw error;

      setNewComment('');
      setNewTopic('');
      setIsSecretComment(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Use existing evaluation if available, otherwise use the first evaluation
      const evaluationId = existingEvaluation?.id || allEvaluations[0].id;

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId,
          user_id: user.id,
          user_type: 'evaluator',
          content: replyContent.trim(),
          is_secret: isSecretReply,
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      setReplyContent('');
      setReplyingTo(null);
      setIsSecretReply(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const getCommentAuthorName = (comment: EvaluationComment) => {
    if (comment.user_type === 'candidate') {
      return candidate.username || candidate.name;
    }
    const ev = allEvaluations.find((e: any) => e.id === comment.evaluation_id);
    if (ev && evaluatorById[ev.evaluator_id]) {
      const info = evaluatorById[ev.evaluator_id];
      return info.username || info.name;
    }
    const authorInfo = commentAuthors[comment.user_id];
    if (authorInfo) return authorInfo.username || authorInfo.name;
    return '参加者';
  };

  const handleSetRating = async (value: number) => {
    if (isOwn) return; // 自分の研究は評価不可
    const next = Math.max(0, Math.min(5, value));
    setRating(next);
    try {
      await savePartial({ rating: next });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 800);
      // 集計の即時反映
      await loadAllEvaluations();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const canViewComment = (comment: EvaluationComment, uid: string | null, uemail: string | null) => {
    if (!comment.is_secret) return true;
    if (uid && comment.user_id === uid) return true; // author can see
    // Presenter (candidate) can always see their thread's secret comments
    if (uemail && uemail === candidate.email) return true;
    return false;
  };
  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, level = 0) => (
    <div key={comment.id} className={`${level > 0 ? 'ml-8 border-l-4 border-primary/40 pl-6' : ''} mb-5`}>
      <div
        className="flex justify-start mb-4"
      >
        <div
          className={`max-w-[85%] rounded-lg p-5 shadow-sm transition-all duration-200 hover:shadow-md ${
            comment.user_type === 'evaluator'
              ? 'bg-blue-50 border border-blue-200 text-blue-900'
              : 'bg-green-50 border border-green-200 text-green-900'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                comment.user_type === 'evaluator' ? 'bg-blue-500' : 'bg-green-500'
              }`} />
              <span className="text-sm font-semibold">
                {getCommentAuthorName(comment)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {comment.is_secret && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                  <Lock className="h-3 w-3" />
                  <span className="text-xs font-medium">プライベート</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {formatCommentTime(comment.created_at)}
              </span>
            </div>
          </div>
          
          <div className={`mb-3 ${comment.is_secret ? 'bg-gray-100 border border-gray-300 rounded p-3' : ''}`}>
            <p className="text-base leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">
              {comment.content}
            </p>
          </div>
          
          <div className="flex justify-start">
            <Button
              variant="secondary"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => setReplyingTo(comment.id)}
            >
              <MessageCircle className="h-3 w-3 mr-1" />
              返信を書く
            </Button>
          </div>
        </div>
      </div>
      
      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-4 ml-4">
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">返信を投稿</span>
            </div>
            <Textarea
              placeholder="返信内容を入力してください..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="secret-reply"
                  checked={isSecretReply}
                  onCheckedChange={(checked) => setIsSecretReply(!!checked)}
                />
                <Label htmlFor="secret-reply" className="flex items-center space-x-2 cursor-pointer text-sm">
                  <span className="text-muted-foreground">プライベート返信</span>
                  {isSecretReply && (
                    <Lock className="h-3 w-3 text-orange-500" />
                  )}
                </Label>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                    setIsSecretReply(false);
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSubmitReply(comment.id, comment.topic)}
                  disabled={!replyContent.trim() || commentLoading}
                >
                  {commentLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  返信を投稿
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Nested replies */}
      <div className="space-y-2">
        {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, level + 1))}
      </div>
    </div>
  );
  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const sortedSlides = candidate.slide_images?.sort((a, b) => a.slide_index - b.slide_index) || [];

  // Simple swipe-to-go-back (right swipe) for mobile
  let touchStartX = 0, touchStartY = 0;
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touchStartX = t.clientX;
    touchStartY = t.clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX;
    const dy = Math.abs(t.clientY - touchStartY);
    if (dx > 50 && dy < 40) {
      onBack();
    }
  };

  return (
    <div className="space-y-6" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          一覧に戻る
        </Button>
        
        {existingEvaluation && (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            評価済み
          </Badge>
        )}
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-600">評価を保存しました</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* アクション（上部に配置）: 自分の研究では非表示 */}
      {!isOwn && (
        <Card className="md:sticky md:top-0 z-10 bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <CardHeader className="hidden md:block">
            <CardTitle>アクション</CardTitle>
            <CardDescription>星評価・「いいね」「参加希望」は即保存されます。</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 py-3 md:py-6">
            <div className="space-y-3">
              <div className="space-y-1 md:space-y-2">
                <Label className="hidden md:inline-block text-sm">星評価（0〜5、0は評価なし）</Label>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((i) => (
                    <button
                      key={i}
                      type="button"
                      className="p-1"
                      onClick={() => handleSetRating(i)}
                      aria-label={`星${i}`}
                    >
                      <Star className={`h-5 w-5 md:h-6 md:w-6 ${rating >= i ? 'text-yellow-500 fill-current' : 'text-muted-foreground'}`} />
                    </button>
                  ))}
                </div>
                <div className="hidden md:block">
                {(() => {
                  const rated = allEvaluations.filter((e) => (e.rating || 0) > 0);
                  const count = rated.length;
                  const avg = count > 0 ? (rated.reduce((s, e) => s + Math.min(5, Math.max(0, e.rating || 0)), 0) / count) : 0;
                  return (
                    <p className="text-xs text-muted-foreground mt-1">
                      平均 {avg.toFixed(1)} / 5 ・ 評価数 {count}
                    </p>
                  );
                })()}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="liked-top"
                  checked={liked}
                  onCheckedChange={async (checked) => {
                    const next = !!checked;
                    setLiked(next);
                    setSavingLike(true);
                    setError(null);
                    try {
                      await savePartial({ liked: next });
                    } catch (e: any) {
                      setError(e.message);
                      setLiked(!next);
                    } finally {
                      setSavingLike(false);
                    }
                  }}
                />
                <Label htmlFor="liked-top" className="flex items-center space-x-2 cursor-pointer">
                  <Heart className={`h-4 w-4 ${liked ? 'fill-current text-red-500' : 'text-muted-foreground'}`} />
                  <span>いいね！{savingLike ? '（保存中…）' : ''}</span>
                </Label>
              </div>
            </div>
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="participate-top"
                  checked={wantToParticipate}
                  onCheckedChange={async (checked) => {
                    const next = !!checked;
                    setWantToParticipate(next);
                    setSavingParticipate(true);
                    setError(null);
                    try {
                      await savePartial({ want_to_participate: next });
                    } catch (e: any) {
                      setError(e.message);
                      setWantToParticipate(!next);
                    } finally {
                      setSavingParticipate(false);
                    }
                  }}
                />
                <Label htmlFor="participate-top" className="flex items-center space-x-2 cursor-pointer">
                  <Users className={`h-4 w-4 ${wantToParticipate ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span>参加希望{savingParticipate ? '（保存中…）' : ''}</span>
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Slides */}
      <Card>
        <CardHeader>
          <CardTitle>研究スライド</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedSlides.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">スライドがアップロードされていません</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sortedSlides.map((slide) => (
                <div key={slide.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-foreground">
                      スライド {slide.slide_index + 1}
                    </h3>
                    {slide.slide_index === candidate.key_slide_index && (
                      <Badge className="bg-yellow-500 hover:bg-yellow-600">
                        <Star className="h-4 w-4 mr-1 fill-current" />
                        キースライド
                      </Badge>
                    )}
                  </div>
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden border">
                    <img
                      src={slide.image_url}
                      alt={`スライド ${slide.slide_index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Candidate Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{candidate.research_topic}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">研究者</label>
              <p className="text-foreground font-medium">{candidate.name}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">所属</label>
              <p className="text-foreground font-medium">{candidate.affiliation}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">研究概要</label>
            <p className="text-foreground whitespace-pre-wrap leading-relaxed">{candidate.research_overview}</p>
          </div>

          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">共同研究者:</span>
            <Badge variant={candidate.has_collaborators ? "default" : "secondary"}>
              {candidate.has_collaborators ? 'あり' : 'なし'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Like/Participation section was moved to the top (アクション) */}

      {/* Comments Section - Always show */}
      {(
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              コメント・質問
            </CardTitle>
            <CardDescription>
              研究者とのやり取りができます。トピックごとに整理され、返信も可能です。原則として全員が閲覧可能なディスカッションの場です。プライベートなやり取りが必要な場合はチェックボックスをご利用ください。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Topic-based Comments */}
            <div className="space-y-8 max-h-none overflow-visible md:max-h-[700px] md:overflow-y-auto">
              {comments.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  まだコメントがありません
                </p>
              ) : (
                (() => {
                  const { topicGroups, replies } = organizeComments(comments);
                  return Object.entries(topicGroups).map(([topic, topicComments]) => (
                    <div key={topic} className="border border-border rounded-lg p-6 bg-card shadow-sm">
                      <div className="mb-6 pb-3 border-b border-border">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-lg text-foreground flex items-center">
                            <MessageCircle className="h-5 w-5 text-primary mr-2" />
                            {topic === 'general' ? '💬 一般的な質問・コメント' : `📋 ${topic}`}
                          </h4>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCollapsedTopics((prev) => ({ ...prev, [topic]: !prev[topic] }))}
                            >
                              {collapsedTopics[topic] ? '展開' : '折りたたむ'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewTopic(topic === 'general' ? '' : topic);
                                setNewComment('');
                                document.getElementById('new-comment-input')?.focus();
                              }}
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />
                              コメント追記
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {topicComments.length}件のコメント・返信
                        </p>
                      </div>
                      {!collapsedTopics[topic] && (
                        <div className="space-y-4">
                          {topicComments.map(comment => 
                            renderComment(comment, replies, 0)
                          )}
                        </div>
                      )}
                    </div>
                  ));
                })()
              )}
            </div>

            {/* New Comment/Topic Input */}
            <div className="space-y-4 border-t border-border pt-6 bg-muted/20 rounded-lg p-4">
              <h5 className="font-semibold text-base text-foreground flex items-center">
                <Send className="h-4 w-4 text-primary mr-2" />
                投稿する
              </h5>
              <div className="space-y-3">
                <Label htmlFor="new-topic" className="text-sm font-medium text-foreground">
                  新しいトピック（オプション）
                </Label>
                <Input
                  id="new-topic"
                  placeholder="例: 研究手法について、データ収集について..."
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                />
              </div>
              <Textarea
                id="new-comment-input"
                placeholder="コメントや質問を入力してください（トピックのみの場合は空でも可）..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={4}
                className="resize-none"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="secret-comment"
                    checked={isSecretComment}
                    onCheckedChange={(checked) => setIsSecretComment(!!checked)}
                  />
                  <Label htmlFor="secret-comment" className="flex items-center space-x-2 cursor-pointer">
                    <span className="text-sm text-muted-foreground">
                      公開したくないプライベートなやり取りはこちらをチェックしてください
                    </span>
                    {isSecretComment && (
                      <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                        <Lock className="h-3 w-3" />
                        <span className="text-xs font-medium">プライベート</span>
                      </div>
                    )}
                  </Label>
                </div>
                
                <Button
                  onClick={handleSubmitComment}
                  disabled={(!newComment.trim() && !newTopic.trim()) || commentLoading}
                >
                  {commentLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  投稿する
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
